import { memo } from "react";

import { Box, TableCell } from "@mui/material";

import { ConditionalLink } from "../../buttons";
import { Checkbox } from "../../input";
import { BODY_CELL_SX, LEADING_COL_WIDTH, RowDivider, StyledTableRow, colFlexAlign, getCellInnerSx } from "../cell";
import { useLayoutContext, useSelectionContext } from "../contexts";
import { TABLE_CTA_COLUMN_KEY, TableColumn } from "../types";

interface ItemRowProps<TDetail> {
    columns: TableColumn<TDetail>[];
    data: TDetail;
    index: number;
}

function ItemRowImpl<TDetail>({ columns, data, index }: ItemRowProps<TDetail>) {
    const { summaryColumnCount, leadingCells, divider, detailBackground, hoverBackground, onDetailRowNavigatePath } =
        useLayoutContext();
    const selection = useSelectionContext();
    const totalColSpan = summaryColumnCount + leadingCells;
    const itemRow = { data, key: index };

    const isSelected = !!selection && selection.isSelected(data);
    const navigatePath = onDetailRowNavigatePath?.(data);
    const isLinkRow = !!navigatePath;
    const hasCheckbox = !!selection;

    return (
        <StyledTableRow
            sx={{
                position: "relative",
                backgroundColor: detailBackground,
                ...(isLinkRow && {
                    cursor: "pointer",
                    "&:hover": { backgroundColor: hoverBackground }
                })
            }}
        >
            {selection && (
                <TableCell sx={{ ...BODY_CELL_SX, width: LEADING_COL_WIDTH }}>
                    <RowDivider color={divider} />
                    <Box sx={getCellInnerSx({ colIdx: 0, totalColSpan, isGrouped: true, hasCheckbox })}>
                        <span onClick={(e) => e.stopPropagation()}>
                            <Checkbox checked={isSelected} onChange={() => selection.toggle(data)} />
                        </span>
                    </Box>
                </TableCell>
            )}
            <TableCell sx={{ ...BODY_CELL_SX, width: LEADING_COL_WIDTH }}>
                {!selection && <RowDivider color={divider} />}
                <Box
                    sx={getCellInnerSx({
                        colIdx: leadingCells - 1,
                        totalColSpan,
                        isGrouped: true,
                        hasCheckbox
                    })}
                />
            </TableCell>
            {columns.map((col, j) => {
                const colIdx = j + leadingCells;
                const isCta = col.dataIndex === TABLE_CTA_COLUMN_KEY;
                const to = isCta || !isLinkRow ? undefined : navigatePath;
                return (
                    <TableCell
                        key={col.dataIndex}
                        align={col.align ?? "left"}
                        width={col.width ?? "auto"}
                        sx={BODY_CELL_SX}
                    >
                        <ConditionalLink
                            to={to}
                            sx={{
                                ...getCellInnerSx({ colIdx, totalColSpan, isGrouped: true, hasCheckbox }),
                                justifyContent: colFlexAlign(col)
                            }}
                        >
                            {col.render(itemRow, index)}
                        </ConditionalLink>
                    </TableCell>
                );
            })}
        </StyledTableRow>
    );
}

export const ItemRow = memo(ItemRowImpl) as <TDetail>(props: ItemRowProps<TDetail>) => JSX.Element;
